/** Planar soft-lock selection for player weapons (simulation-pure). */

import { AIM_LOCK_HALF_ANGLE_RAD } from "./constants";
import { clamp, dist2, fromAngle } from "./math";

export type AssistEnemy = {
  id: number;
  x: number;
  y: number;
  elite?: boolean;
  isAce?: boolean;
  telegraphTimer?: number;
};

export type SoftLockTarget = {
  id: number;
  x: number;
  y: number;
  dist: number;
};

export type SoftLockQuery = {
  x: number;
  y: number;
  facing: number;
  rangeProxy: number;
  halfAngleRad?: number;
};

/** Nearest living enemy in the aim cone within rangeProxy (planar). */
export function findSoftLockTarget(
  query: SoftLockQuery,
  enemies: readonly AssistEnemy[],
): SoftLockTarget | null {
  const halfAngle = query.halfAngleRad ?? AIM_LOCK_HALF_ANGLE_RAD;
  const facing = fromAngle(query.facing);
  let best: SoftLockTarget | null = null;

  for (const enemy of enemies) {
    const dx = enemy.x - query.x;
    const dy = enemy.y - query.y;
    const dist = Math.hypot(dx, dy);
    if (dist < 1e-4 || dist > query.rangeProxy) continue;
    const nx = dx / dist;
    const ny = dy / dist;
    const dot = nx * facing.x + ny * facing.y;
    if (dot <= 0) continue;
    const angle = Math.acos(clamp(dot, -1, 1));
    if (angle > halfAngle) continue;

    if (!best || dist < best.dist) {
      best = {
        id: enemy.id,
        x: enemy.x,
        y: enemy.y,
        dist,
      };
    }
  }
  return best;
}

/** Weapon range proxy used for soft-lock (slightly short of max travel). */
export function weaponRangeProxy(projectileSpeed: number, life: number): number {
  return projectileSpeed * life * 0.85;
}

/** Planar distance helper re-export for cue ranking tests. */
export function planarDist(a: { x: number; y: number }, b: { x: number; y: number }): number {
  return dist2(a, b);
}
